import { useEffect, useState } from 'react';
import { getCookie } from '@/lib/cookies';
import { GEO_STATE_COOKIE, DEFAULT_STATE_USA } from '@/constants/usStates';

/**
 * Custom hook for reading the user's US state.
 *
 * The state is resolved at the edge by `middleware.ts` (from Vercel's geolocation)
 * and written to the `geo_state` cookie. We read it in a **two-phase** way:
 *
 * 1. Initial value is `null` so SSG/SSR and the first client render agree (the page
 *    falls back to 'US' in `data.stateUSA`). This keeps hydration clean — the cookie
 *    is unreadable server-side, so a synchronous read would mismatch.
 * 2. An effect reads the cookie after mount and updates state to the corrected value
 *    ('California'). The SSG HTML already paints 'US' before hydration, so a layout
 *    effect would not prevent that initial render from being visible — a plain effect
 *    yields the same user-visible result without the SSR-only no-op warning.
 *
 * There is no async fetch, so `isLoading` is always `false` (kept for return-shape
 * compatibility). The Feathery form does NOT rely on this hook for its initial value
 * — it reads the cookie synchronously itself at mount (it is client-only), so it is
 * never seeded with the pre-effect 'US' fallback.
 *
 * @param enabled - Whether geo is needed for this page (Builder page flag).
 * @returns stateUSA (full state name, 'US' for non-US/unknown, or null when disabled)
 */
export function useGeoData(enabled: boolean): { stateUSA: string | null; isLoading: boolean } {
	const [stateUSA, setStateUSA] = useState<string | null>(null);

	useEffect(() => {
		setStateUSA(enabled ? getCookie(GEO_STATE_COOKIE) || DEFAULT_STATE_USA : null);
	}, [enabled]);

	return { stateUSA, isLoading: false };
}
